/** Half-hour slots from 00:00 through 23:30 (HH:mm). */
export function buildThirtyMinuteTimeOptions(): string[] {
  const options: string[] = [];
  for (let hour = 0; hour < 24; hour += 1) {
    for (const minute of [0, 30]) {
      options.push(`${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`);
    }
  }
  return options;
}

export const THIRTY_MINUTE_TIME_OPTIONS = buildThirtyMinuteTimeOptions();

/** Snap API or manual values to the nearest 30-minute slot (HH:mm). */
export function snapToThirtyMinuteSlot(value: string | null | undefined): string {
  if (!value) {
    return '08:00';
  }
  const parts = value.split(':');
  const hour = Math.min(23, Math.max(0, Number.parseInt(parts[0] ?? '8', 10) || 0));
  const minute = Number.parseInt(parts[1] ?? '0', 10) || 0;
  const snappedMinute = minute < 15 ? 0 : minute < 45 ? 30 : 0;
  const snappedHour = minute >= 45 ? (hour + 1) % 24 : hour;
  return `${String(snappedHour).padStart(2, '0')}:${String(snappedMinute).padStart(2, '0')}`;
}

export type TimePeriod = 'AM' | 'PM';

export const HOUR_12_OPTIONS = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11] as const;

export const MINUTE_INTERVAL_OPTIONS = [0, 30] as const;

/** Parse HH:mm into 12-hour picker parts. */
export function parseTimeSlotParts(hhmm: string): {
  hour12: number;
  minute: 0 | 30;
  period: TimePeriod;
} {
  const normalized = snapToThirtyMinuteSlot(hhmm);
  const [hourPart, minutePart] = normalized.split(':');
  const hour24 = Number.parseInt(hourPart ?? '0', 10) || 0;
  const minute = (Number.parseInt(minutePart ?? '0', 10) || 0) >= 30 ? 30 : 0;
  const period: TimePeriod = hour24 >= 12 ? 'PM' : 'AM';
  const hour12 = hour24 % 12 || 12;
  return { hour12, minute, period };
}

/** Build HH:mm from 12-hour picker parts. */
export function buildTimeSlot(hour12: number, minute: 0 | 30, period: TimePeriod): string {
  let hour24 = hour12 % 12;
  if (period === 'PM') {
    hour24 += 12;
  }
  return `${String(hour24).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

/** 12-hour label for a HH:mm slot, e.g. "8:00 AM". */
export function formatTimeSlotLabel(hhmm: string): string {
  const [hourPart, minutePart] = hhmm.split(':');
  const hour24 = Number.parseInt(hourPart ?? '0', 10) || 0;
  const minute = Number.parseInt(minutePart ?? '0', 10) || 0;
  const period = hour24 >= 12 ? 'PM' : 'AM';
  const hour12 = hour24 % 12 || 12;
  return `${hour12}:${String(minute).padStart(2, '0')} ${period}`;
}

/** Normalise API time for display (12-hour, ET context). */
export function formatScheduledTimeForDisplay(value: string | null | undefined): string {
  if (!value) {
    return '—';
  }
  return formatTimeSlotLabel(snapToThirtyMinuteSlot(value));
}
